import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AuthProvider, useAuth } from './hooks/useAuth';

// Public
import SignupPage from './pages/SignupPage';
import TenantLoginPage from './pages/TenantLoginPage';
import LandlordLoginPage from './pages/LandlordLoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import GetStartedPage from './pages/GetStartedPage';
import EnrollSuccessPage from './pages/EnrollSuccessPage';
import EnrollCancelledPage from './pages/EnrollCancelledPage';

// Tenant
import TenantDashboard from './pages/tenant/Dashboard';
import TenantBilling from './pages/tenant/Billing';
import TenantPlan from './pages/tenant/Plan';
import TenantSupport from './pages/tenant/Support';
import TenantSettings from './pages/tenant/Settings';

// Landlord
import LandlordDashboard from './pages/landlord/Dashboard';
import LandlordProperty from './pages/landlord/Property';
import LandlordRevenue from './pages/landlord/Revenue';
import LandlordTenants from './pages/landlord/Tenants';

// Employee
import EmployeeLoginPage from './pages/employee/EmployeeLoginPage';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import LandlordPortfolio from './pages/employee/LandlordPortfolio';
import EmployeeLandlords from './pages/employee/EmployeeLandlords';
import EmployeeTenants from './pages/employee/EmployeeTenants';
import EmployeeTickets from './pages/employee/EmployeeTickets';
import ServiceControl from './pages/employee/ServiceControl';
import Documents from './pages/employee/Documents';
import Campaigns from './pages/employee/Campaigns';
import PaymentAlerts from './pages/employee/PaymentAlerts';
import StaffManagement from './pages/employee/StaffManagement';
import BillingPayouts from './pages/employee/BillingPayouts';
import ServicePlans from './pages/employee/ServicePlans';
import EmployeeMessages from './pages/employee/EmployeeMessages';
import AdminReports from './pages/employee/AdminReports';
import MarketingMaterials from './pages/employee/MarketingMaterials';
import BulkImport from './pages/employee/BulkImport';
import LandlordMessages from './pages/landlord/LandlordMessages';
import LandlordReports from './pages/landlord/LandlordReports';
import ISPs from './pages/employee/ISPs';
import PropertyDetail from './pages/employee/PropertyDetail';

// Admin
import AdminPanel from './pages/admin/AdminPanel';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TenantRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user || role !== 'tenant') return <Navigate to="/login" replace />;
  return children;
};

const LandlordRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user || role !== 'landlord') return <Navigate to="/landlord/login" replace />;
  return children;
};

const EmployeeRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  const employeeRoles = ['super_admin', 'operations', 'billing', 'marketing', 'account_manager'];
  if (!user || !employeeRoles.includes(role)) return <Navigate to="/employee/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user || role !== 'admin') return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Elements stripe={stripePromise}>
          <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/signup/:propertySlug" element={<SignupPage />} />
            <Route path="/login" element={<TenantLoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/landlord/login" element={<LandlordLoginPage />} />
            <Route path="/landlord/reset-password" element={<ResetPasswordPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/enroll-success" element={<EnrollSuccessPage />} />
            <Route path="/enroll-cancelled" element={<EnrollCancelledPage />} />
            <Route path="/employee/login" element={<EmployeeLoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* Tenant portal */}
            <Route path="/tenant/dashboard" element={<TenantRoute><TenantDashboard /></TenantRoute>} />
            <Route path="/tenant/billing" element={<TenantRoute><TenantBilling /></TenantRoute>} />
            <Route path="/tenant/plan" element={<TenantRoute><TenantPlan /></TenantRoute>} />
            <Route path="/tenant/support" element={<TenantRoute><TenantSupport /></TenantRoute>} />
            <Route path="/tenant/settings" element={<TenantRoute><TenantSettings /></TenantRoute>} />

            {/* Landlord portal */}
            <Route path="/landlord/dashboard" element={<LandlordRoute><LandlordDashboard /></LandlordRoute>} />
            <Route path="/landlord/properties/:id" element={<LandlordRoute><LandlordProperty /></LandlordRoute>} />
            <Route path="/landlord/messages" element={<LandlordRoute><LandlordMessages /></LandlordRoute>} />
            <Route path="/landlord/reports" element={<LandlordRoute><LandlordReports /></LandlordRoute>} />
            <Route path="/landlord/revenue" element={<LandlordRoute><LandlordRevenue /></LandlordRoute>} />
            <Route path="/landlord/tenants" element={<LandlordRoute><LandlordTenants /></LandlordRoute>} />

            {/* Employee portal */}
            <Route path="/employee/dashboard" element={<EmployeeRoute><EmployeeDashboard /></EmployeeRoute>} />
            <Route path="/employee/landlords" element={<EmployeeRoute><EmployeeLandlords /></EmployeeRoute>} />
            <Route path="/employee/landlords/:id" element={<EmployeeRoute><LandlordPortfolio /></EmployeeRoute>} />
            <Route path="/employee/tenants" element={<EmployeeRoute><EmployeeTenants /></EmployeeRoute>} />
            <Route path="/employee/tickets" element={<EmployeeRoute><EmployeeTickets /></EmployeeRoute>} />
            <Route path="/employee/service" element={<EmployeeRoute><ServiceControl /></EmployeeRoute>} />
            <Route path="/employee/documents" element={<EmployeeRoute><Documents /></EmployeeRoute>} />
            <Route path="/employee/properties/:id" element={<EmployeeRoute><PropertyDetail /></EmployeeRoute>} />
            <Route path="/employee/isps" element={<EmployeeRoute><ISPs /></EmployeeRoute>} />
            <Route path="/employee/plans" element={<EmployeeRoute><ServicePlans /></EmployeeRoute>} />
            <Route path="/employee/billing" element={<EmployeeRoute><BillingPayouts /></EmployeeRoute>} />
            <Route path="/employee/campaigns" element={<EmployeeRoute><Campaigns /></EmployeeRoute>} />
            <Route path="/employee/payment-alerts" element={<EmployeeRoute><PaymentAlerts /></EmployeeRoute>} />
            <Route path="/employee/staff" element={<EmployeeRoute><StaffManagement /></EmployeeRoute>} />
            <Route path="/employee/messages" element={<EmployeeRoute><EmployeeMessages /></EmployeeRoute>} />
            <Route path="/employee/marketing" element={<EmployeeRoute><MarketingMaterials /></EmployeeRoute>} />
             <Route path="/employee/reports" element={<EmployeeRoute><AdminReports /></EmployeeRoute>} />
            <Route path="/employee/bulk-import" element={<EmployeeRoute><BulkImport /></EmployeeRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />

            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Elements>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
